import { Avatar, AvatarGroup, Box, IconButton, Typography } from '@mui/material';
import { Participant } from './MessagingSection';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { UnreadPill } from './UnreadPill';

export const MessagingSectionHeader: React.FC<{
  iconSource: string;
  title: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  participants: Participant[];
  accountabilityPartners?: number[];
  iconSize?: number;
  iconTitleGap?: number;
}> = ({
  iconSource,
  title,
  count,
  open,
  onToggle,
  participants,
  accountabilityPartners = [],
  iconSize = 21,
  iconTitleGap = 10,
}) => {
  const avatarSize = 32;
  const leftIndent = iconSize + iconTitleGap; // aligns avatars with title start

  const roleBadgeFor = (p: Participant): string | null => {
    const roleLower = p.CohortUser.role?.toLowerCase?.() || '';
    if (accountabilityPartners.includes(p.CohortUser.id)) return '/assets/partner-badge.png';
    if (roleLower === 'coach') return '/assets/coach-badge.png';
    if (roleLower === 'captain') return '/assets/captain-badge.png';
    return null;
  };

  return (
    <Box onClick={onToggle}>
      {/* Row 1: icon + title (left), unread pill + chevron (right) */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: `${iconTitleGap}px` }}>
          <img src={iconSource} alt={`${title} icon`} width={iconSize} height={iconSize} />
          <Typography
            sx={{
              color: 'primary.main',
              fontSize: '18px',
              fontWeight: 500,
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          {/* Unread pill if closed and has unread messages */}
          {!open && count > 0 && <UnreadPill count={count} open={open} />}
          {/* Expand/collapse */}
          <IconButton size="small" sx={{ padding: 0 }}>
            {open ? (
              <ExpandLess sx={{ fontSize: 40, stroke: '#fff', strokeWidth: 1, padding: 0 }} />
            ) : (
              <ExpandMore sx={{ fontSize: 40, stroke: '#fff', strokeWidth: 1 }} />
            )}
          </IconButton>
        </Box>
      </Box>
      {/* Row 2: avatars, aligned with title using padding-left */}
      {!open && (
        <Box sx={{ marginTop: '0', paddingLeft: `${leftIndent}px` }}>
          <AvatarGroup
            max={4}
            spacing={15}
            sx={{
              flexDirection: 'row-reverse',
              justifyContent: 'start',

              // avatar sizing & border
              '& .MuiAvatar-root': {
                width: avatarSize,
                height: avatarSize,
                border: '2px solid #fff',
                backgroundColor: '#fff', // Ensures avatars with transparency have a default background
              },

              '& .MuiAvatar-colorDefault': {
                zIndex: 1000, // ensures +5 etc. avatars are on top
                backgroundColor: '#42797C',
                fontSize: '14px',
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 600,
              },
            }}
          >
            {participants.map((p, index) => {
              const badge = roleBadgeFor(p);
              return (
                <Avatar
                  key={p.id}
                  alt={p.name}
                  src={p.avatarUrl || undefined}
                  slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
                  sx={{
                    zIndex: index + 1,
                    // Create role badge with a pseudo-element
                    ...(badge && {
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        bottom: 0,
                        width: avatarSize,
                        height: avatarSize,
                        backgroundImage: `url(${badge})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'contain',
                        pointerEvents: 'none',
                      },
                    }),
                  }}
                />
              );
            })}
          </AvatarGroup>
        </Box>
      )}
    </Box>
  );
};
